// API Service - Connects to Node.js backend
import backendApi from './backendApi';

class ApiService {
    constructor() {
        this.token = sessionStorage.getItem('access_token');
        this.users = [];
        this.sites = JSON.parse(localStorage.getItem('sites') || '[]');
        this.dgrData = JSON.parse(localStorage.getItem('dgrData') || '[]');

        // Initialize with default data if empty
        if (this.sites.length === 0) {
            this.sites = [
                { id: 1, name: 'ACSEPL Site 1', type: 'ACSEPL' },
                { id: 2, name: 'WTC Site 1', type: 'WTC' },
                { id: 3, name: 'LAP Site 1', type: 'LAP' }
            ];
            localStorage.setItem('sites', JSON.stringify(this.sites));
        }

    }

    setToken(token) {
        this.token = token;
        sessionStorage.setItem('access_token', token);
    }

    // Simulate API delay
    async delay(ms = 500) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    // Authentication - Instagram-like login (username + password only)
    async login(username, password, siteName = null, siteId = null, companyCode = null) {
        try {
            // Only log in development mode, and never log passwords
            if (process.env.NODE_ENV === 'development') {
}

            // First try backend API for authentication
            try {
                const response = await backendApi.login(username, password, siteName, siteId, companyCode);

                if (response.success) {
                    if (response.mfa_required) {
                        return {
                            success: true,
                            mfa_required: true,
                            mfa_token: response.mfa_token,
                            message: response.message
                        };
                    }
                    this.setToken(response.token);

                    // Get site information from backend response
                    // Backend returns current_site in response.data.current_site
                    let siteInfo = null;
                    if (response.data && response.data.current_site) {
                        // Use site info from backend response
                        siteInfo = response.data.current_site;
                    } else if (response.user && (response.user.site_id || response.user.site_name)) {
                        // Fallback: construct site info from user data
                        siteInfo = {
                            site_id: response.user.site_id,
                            site_name: response.user.site_name,
                            site_code: response.user.site_code || null
                        };
                    } else if (siteId) {
                        // Fallback: try to find site in mock data if available
                        siteInfo = this.sites.find(s => s.id == siteId) || this.sites[0];
                    } else {
                        siteInfo = this.sites[0]; // Default fallback
                    }

                    return {
                        success: true,
                        token: response.token,
                        user: response.user || response.data?.user,
                        site: siteInfo,
                        data: response.data || null,
                        message: response.message
                    };
                } else {
                    // Backend returned an identifiable error (e.g. 401, 423)
                    const error = new Error(response.error || response.message || 'Login failed');
                    // Copy properties for frontend handling
                    if (response.errorCode) error.code = response.errorCode;
                    if (response.lockout_remaining_minutes) error.lockout_remaining_minutes = response.lockout_remaining_minutes;
                    if (response.remaining_attempts) error.remaining_attempts = response.remaining_attempts;

                    // Throw to be caught by the catch block below
                    throw error;
                }
            } catch (backendError) {
                if (backendError.code === 'CONNECTION_REFUSED' || backendError.message === 'SERVER_NOT_AVAILABLE') {
                    throw new Error('Authentication service unavailable. Please try again.');
                }
                throw backendError;
            }

            throw new Error('Invalid username or password');
        } catch (error) {
            // Only log errors, not sensitive info
            if (process.env.NODE_ENV === 'development') {
                console.error('Login error:', error.message);
            }
            throw error;
        }
    }

    // Get Active Companies
    async getActiveCompanies() {
        try {
            return await backendApi.getActiveCompanies();
        } catch (error) {
            console.error('Error fetching companies:', error);
            return { success: false, error: error.message };
        }
    }

    // User Registration (sends OTP)
    async signup(userData) {
        try {
// Use backend API for registration
            const response = await backendApi.signup(userData);

            if (response.success) {
return {
                    success: true,
                    message: response.message,
                    email: response.email,
                    requiresVerification: response.requiresVerification
                };
            }
return {
                success: false,
                error: response.error
            };
        } catch (error) {
            console.error('Signup error:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Verify OTP
    async verifyOTP(email, otp) {
        try {
            return await backendApi.verifyOTP(email, otp);
        } catch (error) {
            console.error('OTP verification error:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Resend OTP
    async resendOTP(email) {
        try {
            return await backendApi.resendOTP(email);
        } catch (error) {
            console.error('Resend OTP error:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Update User Profile
    async updateProfile(userId, profileData) {
        return backendApi.updateProfile({ ...profileData, user_id: userId });
    }

    // Change Password
    async changePassword(userId, currentPassword, newPassword) {
        return backendApi.changePassword({
            user_id: userId,
            current_password: currentPassword,
            new_password: newPassword,
        });
    }

    // Update Security Settings
    async updateSecuritySettings(userId, securitySettings) {
        await this.delay();

        const user = this.users.find(u => u.id === userId);
        if (!user) {
            return {
                success: false,
                error: 'User not found'
            };
        }

        // Update security settings
        user.securitySettings = securitySettings;
        user.updatedAt = new Date().toISOString();

        localStorage.setItem('users', JSON.stringify(this.users));

        return {
            success: true,
            message: 'Security settings updated successfully'
        };
    }

    // Sites
    async getSites() {
        await this.delay();
        return {
            success: true,
            sites: this.sites
        };
    }

    // DGR Data Entry
    async saveEntry(siteId, formData, userId = null) {
        await this.delay();

        const entry = {
            id: Date.now(),
            site_id: siteId,
            user_id: userId,
            form_data: formData,
            created_at: new Date().toISOString()
        };

        this.dgrData.push(entry);
        localStorage.setItem('dgrData', JSON.stringify(this.dgrData));

        return {
            success: true,
            message: 'Entry saved successfully',
            entry
        };
    }

    // File Upload (Mock)
    async uploadFile(file, siteId) {
        await this.delay(1000);

        // Simulate file processing
        const fileName = file.name;
        const fileSize = file.size;

        return {
            success: true,
            message: 'File uploaded successfully',
            file: {
                name: fileName,
                size: fileSize,
                uploaded_at: new Date().toISOString()
            }
        };
    }

    // Get DGR Data
    async getData(siteId = null, limit = 50, offset = 0) {
        await this.delay();

        let filteredData = this.dgrData;

        if (siteId) {
            filteredData = this.dgrData.filter(entry => entry.site_id == siteId);
        }

        const paginatedData = filteredData.slice(offset, offset + limit);

        return {
            success: true,
            data: paginatedData,
            total: filteredData.length,
            limit,
            offset
        };
    }

    // Password Reset - Request reset token
    async forgotPassword(email, siteId = null, siteName = null) {
        try {
// Try backend API first
            const response = await backendApi.forgotPassword(email, siteId, siteName);

            if (response.success) {
return response;
            }

            throw new Error(response.error || 'Password reset failed');
        } catch (error) {
            console.error('Forgot password error:', error);
            throw error;
        }
    }

    // Password Reset - Reset with token
    async resetPassword(token, password, passwordConfirm, siteId = null) {
        try {
// Try backend API first
            const response = await backendApi.resetPassword(token, password, passwordConfirm || password, siteId);

            if (response.success) {
this.setToken(response.token);
                return response;
            }

            throw new Error(response.error || 'Password reset failed');
        } catch (error) {
            console.error('Reset password error:', error);
            throw error;
        }
    }

    // MFA / Microsoft Authenticator methods
    async verifyLoginMFA(mfaToken, code) {
        try {
            const response = await backendApi.verifyLoginMFA(mfaToken, code);
            if (response.success && response.token) {
                this.setToken(response.token);
                let siteInfo = null;
                if (response.data && response.data.current_site) {
                    siteInfo = response.data.current_site;
                } else if (response.user && (response.user.site_id || response.user.site_name)) {
                    siteInfo = {
                        site_id: response.user.site_id,
                        site_name: response.user.site_name,
                        site_code: response.user.site_code || null
                    };
                } else {
                    siteInfo = this.sites[0];
                }
                return {
                    success: true,
                    token: response.token,
                    user: response.user || response.data?.user,
                    site: siteInfo,
                    data: response.data || null,
                    message: response.message
                };
            }
            return response;
        } catch (error) {
            console.error('MFA verification error:', error);
            throw error;
        }
    }

    async getMFAStatus() {
        return await backendApi.getMFAStatus();
    }

    async setupMFA(regenerate = false) {
        return await backendApi.setupMFA(regenerate);
    }

    async confirmMFASetup(code) {
        return await backendApi.confirmMFASetup(code);
    }

    async disableMFA(password, code = '') {
        return await backendApi.disableMFA(password, code);
    }

    // Logout
    logout() {
        this.token = null;
        sessionStorage.removeItem('access_token');
    }
}

export default new ApiService();

